'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { OutcomeDot } from '@/components/shared/outcome-indicator';
import { SelectField, type SelectFieldItem } from '@/components/ui/select-field';
import { outcomeHueToStepVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';

// ── Types ────────────────────────────────────────────────────────────────────

interface OutcomeOption {
  index: number;
  label: string;
  /** Optional outcome image URL from backend. */
  imageUrl?: string | undefined;
  /** NSFW moderation flag for the outcome image (spec 071). */
  imageNsfw?: boolean | undefined;
}
interface OutcomePillsProps {
  /** Parent market / proposal pubkey — seeds the outcome indicator's pattern lib. */
  marketPubkey: string;
  outcomes: OutcomeOption[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** Optional prefix label shown inside the button (e.g. "From", "To"). */
  prefixLabel?: string;
  /** When true, the trigger button renders without its own border. */
  borderless?: boolean;
  /** Pre-formatted balance strings keyed by outcome index, shown in dropdown rows. */
  outcomeBalances?: Readonly<Record<number, string>>;
  /** Show outcome images in indicators. Default: true. Set false for trade modal. */
  showImages?: boolean;
  /** Extra classes for the underlying Select trigger (e.g. surface bg override). */
  triggerClassName?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Outcome selector — built on Radix Select so it works inside SilkSheet on
 * mobile (Radix Popover gets its touch events swallowed by Silk's gesture
 * handlers; Radix Select portals to body and escapes that scope).
 *
 * Hidden when there are no outcomes; renders a static label when only one.
 */
export function OutcomePills(t0) {
  const $ = _c(61);
  const {
    marketPubkey,
    outcomes,
    selectedIndex,
    onSelect,
    prefixLabel,
    borderless: t1,
    outcomeBalances,
    showImages: t2,
    triggerClassName
  } = t0;
  const borderless = t1 === undefined ? false : t1;
  t2 === undefined ? true : t2;
  const t = useTranslations("trade");
  let realOutcomes;
  let t3;
  let t4;
  if ($[0] !== outcomes) {
    realOutcomes = outcomes.filter(_temp);
    t3 = useOutcomeColor;
    t4 = realOutcomes.map(_temp2);
    $[0] = outcomes;
    $[1] = realOutcomes;
    $[2] = t3;
    $[3] = t4;
  } else {
    realOutcomes = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== marketPubkey) {
    t5 = {
      marketPubkey
    };
    $[4] = marketPubkey;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const colorClasses = t3(t4, t5);
  let colorByIndex;
  if ($[6] !== colorClasses || $[7] !== realOutcomes) {
    colorByIndex = {};
    for (let i = 0; i < realOutcomes.length; i++) {
      const o_1 = realOutcomes[i];
      const cls = colorClasses[i];
      if (o_1 !== undefined && cls !== undefined) {
        colorByIndex[o_1.index] = outcomeHueToStepVar(cls.hue, 400);
      }
    }
    $[6] = colorClasses;
    $[7] = realOutcomes;
    $[8] = colorByIndex;
  } else {
    colorByIndex = $[8];
  }
  if (outcomes.length === 0) {
    return null;
  }
  const hasMultiple = outcomes.length > 1;
  let t6;
  if ($[9] !== outcomes || $[10] !== selectedIndex) {
    let t7;
    if ($[12] !== selectedIndex) {
      t7 = o_2 => o_2.index === selectedIndex;
      $[12] = selectedIndex;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = outcomes.find(t7);
    $[9] = outcomes;
    $[10] = selectedIndex;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const selected = t6;
  if (!hasMultiple) {
    const isAllSelected = selected !== undefined && selected.index < 0;
    const selectedDotColor = selected !== undefined && !isAllSelected ? colorByIndex[selected.index] ?? "" : undefined;
    const selectedBalance = selected !== undefined ? outcomeBalances?.[selected.index] : undefined;
    const t7 = borderless ? "px-3 py-2" : "h-10 rounded-control border border-border-primary bg-background-secondary px-3";
    let t8;
    if ($[14] !== t7) {
      t8 = cn("flex w-full items-center justify-between gap-2 text-body-lg", t7);
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== prefixLabel) {
      t9 = prefixLabel !== undefined && prefixLabel.length > 0 && <span className="shrink-0 text-caption font-normal text-foreground-secondary">{prefixLabel}</span>;
      $[16] = prefixLabel;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== isAllSelected || $[19] !== selectedDotColor) {
      t10 = !isAllSelected && selectedDotColor !== undefined && <OutcomeDot color={selectedDotColor} />;
      $[18] = isAllSelected;
      $[19] = selectedDotColor;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== selected?.label || $[22] !== t) {
      t11 = selected?.label ?? t("select_outcome");
      $[21] = selected?.label;
      $[22] = t;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== t11) {
      t12 = <span className="truncate">{t11}</span>;
      $[24] = t11;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    let t13;
    if ($[26] !== t10 || $[27] !== t12 || $[28] !== t9) {
      t13 = <div className="flex min-w-0 items-center gap-2 truncate">{t9}{t10}{t12}</div>;
      $[26] = t10;
      $[27] = t12;
      $[28] = t9;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== selectedBalance) {
      t14 = selectedBalance !== undefined && <span className="shrink-0 text-caption tabular-nums text-foreground-secondary">{selectedBalance}</span>;
      $[30] = selectedBalance;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t13 || $[33] !== t14 || $[34] !== t8) {
      t15 = <div className={t8}>{t13}{t14}</div>;
      $[32] = t13;
      $[33] = t14;
      $[34] = t8;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    return t15;
  }
  let t7;
  if ($[36] !== colorByIndex || $[37] !== outcomeBalances || $[38] !== outcomes) {
    let t8;
    if ($[40] !== colorByIndex || $[41] !== outcomeBalances) {
      t8 = outcome => {
        const isAllOption = outcome.index < 0;
        const dotStyle = colorByIndex[outcome.index] ?? "";
        const balance = outcomeBalances?.[outcome.index];
        return {
          value: outcome.index.toString(),
          label: <span className="flex w-full min-w-0 items-baseline gap-2">{!isAllOption && <OutcomeDot color={dotStyle} />}<span className="truncate">{outcome.label}</span>{balance !== undefined && <span className="ml-auto shrink-0 text-caption tabular-nums text-foreground-secondary">{balance}</span>}</span>
        };
      };
      $[40] = colorByIndex;
      $[41] = outcomeBalances;
      $[42] = t8;
    } else {
      t8 = $[42];
    }
    t7 = outcomes.map(t8);
    $[36] = colorByIndex;
    $[37] = outcomeBalances;
    $[38] = outcomes;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  const items = t7;
  let t8;
  if ($[43] !== selectedIndex) {
    t8 = selectedIndex.toString();
    $[43] = selectedIndex;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  let t9;
  if ($[45] !== onSelect) {
    t9 = v => {
      onSelect(Number(v));
    };
    $[45] = onSelect;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== t) {
    t10 = t("select_outcome");
    $[47] = t;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== prefixLabel) {
    t11 = prefixLabel !== undefined && prefixLabel.length > 0 ? {
      label: prefixLabel
    } : {};
    $[49] = prefixLabel;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  const t12 = borderless && "border-0 bg-transparent shadow-none";
  let t13;
  if ($[51] !== t12 || $[52] !== triggerClassName) {
    t13 = cn(t12, triggerClassName);
    $[51] = t12;
    $[52] = triggerClassName;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== items || $[55] !== t10 || $[56] !== t11 || $[57] !== t13 || $[58] !== t8 || $[59] !== t9) {
    t14 = <SelectField value={t8} onValueChange={t9} items={items} placeholder={t10} {...t11} triggerClassName={t13} />;
    $[54] = items;
    $[55] = t10;
    $[56] = t11;
    $[57] = t13;
    $[58] = t8;
    $[59] = t9;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  return t14;
}
function _temp2(o_0) {
  return {
    title: o_0.label,
    index: o_0.index
  };
}
function _temp(o) {
  return o.index >= 0;
}